"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { marked } from "marked";
import { sanitizeHtml } from "@/lib/sanitize";

import {
  ArrowLeftIcon,
  ClockIcon,
  UserIcon,
  HeartIcon,
  ShareIcon,
  EyeIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useBlogDetail, useLikeBlog } from "@/hooks/useBlogs";
import { Footer } from "@/components/Footer";

const BlogPostClient = () => {
  const params = useParams();
  const slug = params.slug as string;

  const { data, isLoading } = useBlogDetail(slug);
  const likeBlog = useLikeBlog();

  const [isLiked, setIsLiked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const blog = data?.blog;
  const relatedBlogs = data?.relatedBlogs || [];

  const handleLike = async () => {
    if (!blog || likeBlog.isPending) return;

    const wasLiked = isLiked;
    setIsLiked(!wasLiked);

    if (!wasLiked) {
      likeBlog.mutate({ slug: blog.slug, blogId: blog._id });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog?.title,
          text: blog?.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px] flex items-center justify-center zoom-container">
        <LoadingSpinner
          fullScreen={false}
          showText={true}
          text="Loading article..."
        />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[90px] zoom-container">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-5 py-3 mb-8 border border-gray-200 shadow-sm">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            <span className="text-gray-700 text-sm font-medium">Not Found</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            Blog Post Not Found
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            The blog post you&apos;re looking for doesn&apos;t exist or has been
            removed.
          </p>
          <Link
            href="/blogs"
            className="inline-flex items-center gap-2 text-gray-700 hover:text-orange-500 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[90px] zoom-container">
        {/* Navigation Bar */}
        <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-[60px] sm:top-[80px] z-40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <Link
                href="/blogs"
                className="flex items-center gap-2 text-gray-900 hover:text-orange-500 transition-all duration-200"
              >
                <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">
                  Back to Blogs
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {/* Main Blog Card */}
          <article className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
            {/* Header */}
            <div className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-orange-500/10 text-orange-600 text-xs sm:text-sm font-semibold rounded-lg">
                      {blog.category}
                    </span>
                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                      <EyeIcon className="w-3 h-3" />
                      {blog.views}
                    </div>
                  </div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                    {blog.title}
                  </h1>
                </div>
              </div>
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="px-4 sm:px-6 py-4 bg-orange-50/50 border-b border-gray-100">
                <p className="text-gray-700 font-medium italic border-l-4 border-orange-500 pl-4 py-1">
                  {blog.excerpt}
                </p>
              </div>
            )}

            {/* Featured Image */}
            {blog.featuredImage && (
              <div className="px-4 sm:px-6 pt-6">
                <div className="relative w-full h-48 sm:h-80 rounded-xl overflow-hidden shadow-md">
                  <Image
                    src={blog.featuredImage}
                    alt={blog.title}
                    fill
                    className="object-cover"
                    priority
                  />
                </div>
              </div>
            )}

            {/* Blog Content */}
            <div className="p-4 sm:p-6">
              <div
                className="prose prose-sm sm:prose-base max-w-none text-gray-900 leading-relaxed
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mb-4 prose-headings:mt-6
                prose-p:text-gray-700 prose-p:mb-4 
                prose-a:text-orange-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline 
                prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
                prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                prose-li:text-gray-700 prose-li:mb-2
                prose-blockquote:border-l-4 prose-blockquote:border-orange-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-700 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:pr-2 prose-blockquote:rounded-r-lg
                prose-strong:font-bold prose-strong:text-gray-900
                prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-xl"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(marked.parse(blog.content || "", { breaks: true }) as string) }}
              />
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="px-4 sm:px-6 pb-4">
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-600 text-xs sm:text-sm rounded-lg hover:bg-orange-50 hover:border-orange-200 hover:text-orange-600 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author & Engagement Section */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-4 border-t border-gray-200">
              {/* Author Info */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold text-sm ring-2 ring-orange-500/20">
                  <UserIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {blog.author.name}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {blog.publishedAt
                      ? new Date(blog.publishedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : new Date(blog.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </p>
                </div>
              </div>

              {/* Engagement Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleLike}
                    className={`flex items-center gap-2 transition-colors ${isLiked
                      ? "text-red-500"
                      : "text-gray-600 hover:text-red-500"
                      }`}
                  >
                    {isLiked ? (
                      <HeartIconSolid className="w-5 h-5" />
                    ) : (
                      <HeartIcon className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">{blog.likes}</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors"
                  >
                    <ShareIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  Updated:{" "}
                  {new Date(blog.updatedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>
          </article>

          {/* Related Blogs */}
          {relatedBlogs.length > 0 && (
            <div className="mt-6 sm:mt-8">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  Related Articles
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog._id}
                    href={`/blogs/${relatedBlog.slug}`}
                    className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200"
                  >
                    <div className="relative">
                      {relatedBlog.featuredImage ? (
                        <div className="relative aspect-[16/9]">
                          <Image
                            src={relatedBlog.featuredImage}
                            alt={relatedBlog.title}
                            fill
                            className="object-cover transform group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                      ) : (
                        <div className="aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          <DocumentTextIcon className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="inline-block bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1 text-xs font-semibold text-gray-700 border border-gray-200/50 shadow-sm">
                          {relatedBlog.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors text-sm">
                        {relatedBlog.title}
                      </h3>
                      <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                        {relatedBlog.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <ClockIcon className="w-3 h-3" />
                          <span>
                            {Math.max(
                              1,
                              Math.ceil(relatedBlog.content.length / 500)
                            )}{" "}
                            min
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <EyeIcon className="w-3 h-3" />
                          <span>{relatedBlog.views}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogPostClient;
